import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type {
  AnalysisType,
  EvinAnalysisResult,
  SuggestedFix,
} from '../../shared/types';
import { suggestFix } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface Props {
  result: EvinAnalysisResult;
  analysisType: AnalysisType;
  /** Persist the generated rewrite onto the analysis so it stays anchored. */
  onSaved?: (fix: SuggestedFix) => void;
}

/**
 * The rewrite comes back as one string with single "\n" line breaks for
 * structure. ReactMarkdown collapses single newlines, packing everything into
 * a dense block. Promote each non-empty line to its own paragraph so sections
 * breathe — more whitespace, one idea per line.
 */
function airier(md: string): string {
  return md
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .join('\n\n');
}

/**
 * Generic "Fix auto" button — appears for any non-conform result.
 * Calls /api/evin/suggest-fix and renders the compliant rewrite.
 */
export default function SuggestFixButton({ result, analysisType, onSaved }: Props) {
  const [loading, setLoading] = useState(false);
  const [suggestion, setSuggestion] = useState<SuggestedFix | null>(
    result.suggestedFix ?? null,
  );
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const { features } = useAuth();

  // Re-bind to the displayed analysis when it changes. React reuses this
  // component instance across analyses (same position in the tree), so we
  // resync from the result: show its saved rewrite if it has one, otherwise
  // clear — preventing one discussion's rewrite from leaking into another.
  useEffect(() => {
    setSuggestion(result.suggestedFix ?? null);
    setError(null);
    setCopied(false);
  }, [result]);

  // Hide the button once the verdict is CONFORME — even if minor "Faible"
  // vigilance points remain, there's nothing to rewrite.
  if (result.verdict === 'CONFORME') return null;

  // Only show the button when there's something to fix
  const hasIssues =
    result.pointsDeVigilance.length > 0 || result.interdictionsFormelles.length > 0;

  if (!hasIssues) return null;

  // Feature gated — not available on free plan
  if (!features.suggestFix) return null;

  const originalContent =
    result.originalInput ??
    result.socialMeta?.caption ??
    '';

  // Can't suggest a fix on image-only input without the original text
  if (!originalContent.trim()) return null;

  const handleSuggest = async () => {
    setLoading(true);
    setError(null);
    try {
      const issues = [
        ...result.interdictionsFormelles.map((i) => ({
          element: i.element,
          explanation: i.explanation,
          legalBasis: i.legalBasis,
        })),
        ...result.pointsDeVigilance.map((p) => ({
          element: p.element,
          explanation: p.explanation,
          legalBasis: p.legalBasis,
        })),
      ];
      const s = await suggestFix({
        analysisType,
        originalContent,
        issues,
        context: result.originalContext,
      });
      setSuggestion(s);
      onSaved?.(s);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (!suggestion?.rewritten) return;
    navigator.clipboard.writeText(suggestion.rewritten);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (suggestion) {
    return (
      <div className="mt-6 p-5 sm:p-6 rounded-xl border border-emerald-200 dark:border-emerald-800/40 bg-emerald-50/60 dark:bg-emerald-900/20">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="font-display text-base text-emerald-900 dark:text-emerald-100 flex items-center gap-2">
              ✨ Version réécrite conforme
            </div>
            <p className="text-xs text-emerald-700/70 dark:text-emerald-300/70 mt-0.5">
              Générée par IA — à relire avant utilisation
            </p>
          </div>
          <button
            onClick={handleCopy}
            title={copied ? 'Copié' : 'Copier'}
            aria-label={copied ? 'Copié' : 'Copier'}
            className="shrink-0 p-2 rounded-lg text-emerald-600/70 dark:text-emerald-300/70 hover:text-emerald-800 dark:hover:text-emerald-200 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-colors"
          >
            {copied ? (
              <svg className="w-4 h-4 text-emerald-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}><polyline points="20 6 9 17 4 12" /></svg>
            ) : (
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
            )}
          </button>
        </div>

        <div className="p-5 sm:p-7 rounded-lg bg-white dark:bg-slate-900/60 border border-emerald-200 dark:border-emerald-800/30 text-[0.9rem] text-slate-800 dark:text-slate-200 mb-3 prose-evin prose-evin-airy">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{airier(suggestion.rewritten)}</ReactMarkdown>
        </div>

        {suggestion.changes.length > 0 && (
          <details className="text-xs">
            <summary className="cursor-pointer text-emerald-800 dark:text-emerald-200 font-medium">
              Voir les {suggestion.changes.length} modification{suggestion.changes.length > 1 ? 's' : ''}
            </summary>
            <ul className="mt-2 space-y-2">
              {suggestion.changes.map((c, i) => (
                <li key={i} className="p-2 rounded bg-white/60 dark:bg-slate-800/40">
                  <div className="text-red-700 dark:text-red-400 line-through">{c.before}</div>
                  <div className="text-emerald-700 dark:text-emerald-400">→ {c.after}</div>
                  <div className="text-slate-600 dark:text-slate-400 mt-0.5 italic">
                    {c.reason}
                  </div>
                </li>
              ))}
            </ul>
          </details>
        )}

        {suggestion.rationale && (
          <details className="mt-3 text-xs">
            <summary className="cursor-pointer text-emerald-800 dark:text-emerald-200 font-medium">
              Méthodologie de réécriture
            </summary>
            <div className="mt-2 text-emerald-800/80 dark:text-emerald-200/80 italic prose-evin-inline">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{suggestion.rationale}</ReactMarkdown>
            </div>
          </details>
        )}
      </div>
    );
  }

  return (
    <div className="mt-6">
      {error && (
        <div className="mb-3 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm">
          {error}
        </div>
      )}
      <button
        onClick={handleSuggest}
        disabled={loading}
        className="w-full py-3 rounded-xl font-display text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-emerald-200 dark:shadow-emerald-900/30 flex items-center justify-center gap-2"
      >
        {loading ? (
          <>
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              />
            </svg>
            Génération en cours…
          </>
        ) : (
          <>✨ Suggérer une version conforme</>
        )}
      </button>
    </div>
  );
}
